import { useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  useGetSuppliersQuery,
  useTransferDeliveryItemsToNewPoMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface TransferToNewPoModalProps {
  isOpen: boolean;
  toggle: () => void;
  purchaseOrder: any;
  onSuccess?: () => void;
}

const ELIGIBLE = new Set(["pending", "partially_delivered"]);

const getRemaining = (item: any): number => {
  const ordered = Number(item?.ordered_quantity ?? item?.quantity ?? 0);
  const delivered = Number(item?.delivered_quantity ?? 0);
  return Math.max(0, ordered - delivered);
};

const TransferToNewPoModal = ({
  isOpen,
  toggle,
  purchaseOrder,
  onSuccess,
}: TransferToNewPoModalProps) => {
  const { data: suppliersResp, isLoading: suppliersLoading } =
    useGetSuppliersQuery({ page: 1, pageSize: 100 } as any, { skip: !isOpen });
  const [transferItems, { isLoading: isSubmitting }] =
    useTransferDeliveryItemsToNewPoMutation();

  const [supplierId, setSupplierId] = useState<string>("");
  const [deliveryDate, setDeliveryDate] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  // item_id -> { selected, quantity }
  const [rows, setRows] = useState<
    Record<number, { selected: boolean; quantity: string }>
  >({});
  const [error, setError] = useState<string | null>(null);

  const suppliers: any[] = useMemo(() => {
    const d: any = suppliersResp;
    return d?.results || d?.data || (Array.isArray(d) ? d : []) || [];
  }, [suppliersResp]);

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s: any) => ({
        value: String(s.id),
        label: s.name || s.company_name || `Supplier ${s.id}`,
      })),
    [suppliers]
  );

  const eligibleItems: any[] = useMemo(() => {
    return (purchaseOrder?.items || []).filter(
      (it: any) =>
        ELIGIBLE.has(it?.delivery_status) &&
        !it?.item_undeliverable &&
        getRemaining(it) > 0
    );
  }, [purchaseOrder?.items]);

  const setRow = (id: number, patch: Partial<{ selected: boolean; quantity: string }>) =>
    setRows((prev) => {
      const current = prev[id] || { selected: false, quantity: "" };
      return { ...prev, [id]: { ...current, ...patch } };
    });

  const handleSubmit = async () => {
    setError(null);
    if (!supplierId) {
      setError("Please select a supplier for the new purchase order.");
      return;
    }
    const items: { item_id: number; quantity: number }[] = [];
    for (const it of eligibleItems) {
      const row = rows[it.id];
      if (!row?.selected) continue;
      const qty = Number(row.quantity);
      const remaining = getRemaining(it);
      if (Number.isNaN(qty) || qty <= 0) {
        setError(`Enter a valid quantity for ${it.product_name || "an item"}.`);
        return;
      }
      if (qty > remaining) {
        setError(
          `Quantity for ${it.product_name || "an item"} exceeds the remaining balance (${remaining}).`
        );
        return;
      }
      items.push({ item_id: it.id, quantity: qty });
    }
    if (items.length === 0) {
      setError("Select at least one item and quantity to transfer.");
      return;
    }
    try {
      const res: any = await transferItems({
        id: purchaseOrder.id,
        new_supplier_id: Number(supplierId),
        items,
        delivery_date: deliveryDate || undefined,
        notes: notes || undefined,
      }).unwrap();
      toast.success(
        `Created ${res?.new_po_number || "new PO"}${
          res?.email_sent ? " and emailed the supplier." : "."
        }`
      );
      setRows({});
      setSupplierId("");
      setDeliveryDate("");
      setNotes("");
      toggle();
      onSuccess?.();
    } catch (err: any) {
      const msg =
        err?.data?.error || err?.data?.detail || "Failed to transfer items.";
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>Transfer Items to a New PO</ModalHeader>
      <ModalBody>
        <p className="text-muted">
          Move still-open (pending / partially delivered) delivery quantities to a
          new purchase order under a different supplier. The new PO needs no
          approval and is emailed to the selected supplier automatically.
        </p>

        <FormGroup>
          <Label className="fw-semibold">
            New Supplier <span className="text-danger">*</span>
          </Label>
          <Select
            classNamePrefix="select"
            isClearable
            isLoading={suppliersLoading}
            placeholder={
              suppliersLoading ? "Loading suppliers..." : "Search a supplier..."
            }
            options={supplierOptions}
            value={
              supplierOptions.find((o) => o.value === supplierId) || null
            }
            onChange={(opt: any) => setSupplierId(opt ? opt.value : "")}
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : undefined
            }
            styles={{
              menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </FormGroup>

        <Label className="fw-semibold">Items to transfer</Label>
        {eligibleItems.length === 0 ? (
          <div className="alert alert-info py-2">
            No pending or partially-delivered items are available to transfer.
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle mb-2" size="sm">
              <thead className="table-light">
                <tr>
                  <th></th>
                  <th>Product</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Remaining</th>
                  <th className="text-center">Transfer Qty</th>
                </tr>
              </thead>
              <tbody>
                {eligibleItems.map((it: any) => {
                  const remaining = getRemaining(it);
                  const row = rows[it.id] || { selected: false, quantity: "" };
                  return (
                    <tr key={it.id}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={row.selected}
                          onChange={(e) =>
                            setRow(it.id, {
                              selected: e.target.checked,
                              quantity: e.target.checked
                                ? row.quantity || String(remaining)
                                : row.quantity,
                            })
                          }
                        />
                      </td>
                      <td>
                        <div className="fw-medium">{it.product_name}</div>
                        {it.measurement_name && (
                          <small className="text-muted">
                            {it.measurement_name}
                          </small>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="badge bg-soft-secondary text-secondary text-uppercase">
                          {it.delivery_status}
                        </span>
                      </td>
                      <td className="text-center">{remaining}</td>
                      <td className="text-center" style={{ width: 120 }}>
                        <Input
                          type="number"
                          min={0}
                          max={remaining}
                          step="any"
                          disabled={!row.selected}
                          value={row.quantity}
                          onChange={(e) =>
                            setRow(it.id, { quantity: e.target.value })
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        <FormGroup>
          <Label className="fw-semibold">Delivery Date (optional)</Label>
          <Input
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label className="fw-semibold">Notes (optional)</Label>
          <Input
            type="textarea"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormGroup>

        {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || eligibleItems.length === 0}
        >
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-1" /> Transferring...
            </>
          ) : (
            "Create New PO & Email"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TransferToNewPoModal;
